import {store} from "../../../Store/index.js";
import {E, NN} from "js-vextensions";
import {StoryMessageProto} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {EffortLevel, ThinkingType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.js";
import {LLMApi} from "../../../Store/main/settings.js";
import {GenerateNextMessage_OpenRouter} from "./OpenRouter.js";
import {GenerateNextMessage_Anthropic} from "./Anthropic.js";

export type GenerateNextMessageOptions_Init = RequiredBy<Partial<GenerateNextMessageOptions>,
	"minNumberedListSize" |
	"dynamicListNumbers" |
	"llmHistory_keepLastXNumberedLists" |
	"temperature" |
	"apiType" |
	"apiKey" |
	"model" |
	"blockTrainingAndRetention" |
	"prevMessages"
>;
export type GenerateNextMessageOptions_Init_UsingSettings = PartialBy<GenerateNextMessageOptions_Init,
	"apiType" | "apiKey" | "model" | "blockTrainingAndRetention"
>;
export class GenerateNextMessageOptions {
	constructor(data: GenerateNextMessageOptions_Init) {
		Object.assign(this, data);
	}
	static New_UsingSettings(data: GenerateNextMessageOptions_Init_UsingSettings) {
		const settings = store.main.settings;
		return new GenerateNextMessageOptions({
			apiType: settings.llm_mainAPI,
			apiKey: settings.llm_mainAPI == LLMApi.openRouter ? NN(settings.openRouterAPIKey) : NN(settings.anthropicAPIKey),
			model: settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouterModel : settings.anthropicModel,
			...(settings.llm_mainAPI == LLMApi.openRouter ? {modelProvidersList: settings.openRouterProviders} : {}),
			blockTrainingAndRetention: settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouter_blockTrainingAndRetention : false,
			...data,
		});
	}

	// left as their defaults here (atm)
	baseURLOverride?: string;
	omitOldNumberedLists = true;

	// from config
	minNumberedListSize!: number;
	dynamicListNumbers!: number[];
	llmHistory_keepLastXNumberedLists!: number;
	temperature!: number;
	thinkingType = ThinkingType.disabled;
	thinking_budgetTokens = 0;
	responseTokens = 4096;
	effort = EffortLevel.high;

	// from story object (mostly at least)
	/** The system prompt to send with the API request. If null/undefined, no system prompt is sent. */
	systemPrompt?: string|n;

	// from settings
	apiType!: LLMApi;
	apiKey!: string;
	model!: string;
	modelProvidersList?: string;
	blockTrainingAndRetention!: boolean;

	// call-specific
	prevMessages!: StoryMessageProto[];
	onTextSegment?: (nextTextSegment: string)=>void;
	onThinkingSegment?: (nextThinkingSegment: string)=>void;
	onOutputTokens?: (outputTokens: number)=>void;
}

export function GenerateNextMessage_Generic(options: GenerateNextMessageOptions_Init_UsingSettings) {
	const opts = GenerateNextMessageOptions.New_UsingSettings(options);
	if (opts.apiType == LLMApi.openRouter) return GenerateNextMessage_OpenRouter(opts);
	if (opts.apiType == LLMApi.anthropic) return GenerateNextMessage_Anthropic(opts);
	throw new Error(`Unsupported LLM API: ${opts.apiType}`);
}

export function CategorizeLines(lines: string[]) {
	return lines.map(line=>CategorizeLine(line));
}
export function CategorizeLine(line: string) {
	const numberedListMatch = line.match(/^\D{0,2}(\d{1,2})\D/);
	const listNumber = numberedListMatch?.[1] != null ? Number(numberedListMatch?.[1]) : null;
	return {
		text: line,
		listNumber,
	};
}
export function GetMessageInfo(message: StoryMessageProto, minNumberedListSize: number) {
	const lines = message.content.split("\n");
	const linesCategorized = CategorizeLines(lines);
	// we never consider messages from the user to be a "numbered list"; if one would seem so, it presumably is just the user trying to modify the engine prompt
	const hasNumberedList = message.role == "assistant" && linesCategorized.filter(a=>a.listNumber != null).length >= minNumberedListSize;
	return {lines, linesCategorized, hasNumberedList};
}

export function ApplyOmitOldNumberedLists(prevMessages: StoryMessageProto[], opts: GenerateNextMessageOptions): StoryMessageProto[] {
	const prevMessages_info = prevMessages.map(message=>GetMessageInfo(message, opts.minNumberedListSize));

	const indexesOfLastXMessagesWithNumberedList = prevMessages_info.filter(a=>a.hasNumberedList).TakeLast(opts.llmHistory_keepLastXNumberedLists).map(a=>prevMessages_info.indexOf(a));

	// for each numbered-list found in the message history, omit it -- *except* for the last such numbered-list (needed ofc, for the user's last choice-input to be recognized)
	return prevMessages.map((message, i)=>{
		const info = prevMessages_info[i];
		if (message.role == "user" || info == null) return message; // never modify user's own messages

		// if this message is not in the last X messages with a numbered-list, but it has a numbered-list, omit those lines
		if (!indexesOfLastXMessagesWithNumberedList.includes(i) && info.hasNumberedList) {
			return {...message, content: info.linesCategorized.filter(a=>a.listNumber == null).map(a=>a.text).join("\n")};
		}

		// if this is in the last X numbered-lists, AND user made a choice/number-selection from this list, omit all dynamic-list options OTHER THAN the choice selected
		// (thus helping avoid the LLM repeating the same dynamic options; the instructions already tell it to avoid this, but it fairly often repeats them anyway)
		// todo: maybe remove this; it *maybe* is increasing the rate at which the LLM does not produce a numbered list at all (albeit, that case is easy to move past)
		/*const nextMessage = prevMessages_final[i + 1];
		const chosenNumber = nextMessage?.role == "user" && IsNumberString(nextMessage.content) ? Number(nextMessage.content) : null;
		if (indexesOfLastXMessagesWithNumberedList.includes(i) && chosenNumber != null) {
			//const chosenNumber_dynamic = chosenNumber != null && opts.dynamicListNumbers.includes(chosenNumber) ? chosenNumber : null;
			return {
				...message,
				content: info.linesCategorized.map(a=>{
					const isDynamicListItem = a.listNumber != null && opts.dynamicListNumbers.includes(a.listNumber);
					if (a.listNumber != null && isDynamicListItem && a.listNumber != chosenNumber) {
						/*const replacementPhrase = `{LLM MUST: Generate a fresh choice in this slot once player responds}`;
						//const replacementPhrase = ``; // LLM seems to give the best result simply by leaving the slot empty
						return `[${a.listNumber}] ${replacementPhrase}`; // keep line-number, else LLM can get confused and omit numbered-entries in next response*#/

						const extraInstruction = g.test1 ?? "{LLM MUST: Forget this choice, and generate something else in its place.}";
						return `${a.text} ${extraInstruction}`;
					}
					return a.text;
				}).join("\n"),
			};
		}*/

		return message;
	});
}

export function InsertSummaryDummyMessages(prevMessages: StoryMessageProto[]): StoryMessageProto[] {
	const result = [...prevMessages];
	for (let i = 0; i < result.length; i++) {
		const prevMessage = result[i - 1];
		const message = result[i];
		if (message.isSummary && prevMessage?.role != "user") {
			result.splice(i, 0, new StoryMessageProto({role: "user", content: "Please include the summary for the next part of the story.", model: null}));
		}
	}
	return result;
}

// helper functions for dev-tools experimenting
export const messages = [] as StoryMessageProto[];

export function LLMTest_MessageDump() {
	return messages.map(a=>a.content).join("\n==========\n");
}

//type Type_GenerateNextMessage_Anthropic = typeof import("./Anthropic.js")["GenerateNextMessage_Anthropic"];
export async function LLMTest_Reset(startPrompt: string, generateFn: typeof GenerateNextMessage_Generic) {
	messages.Clear();
	await LLMTest_Input(startPrompt, generateFn);
}
export async function LLMTest_Input(message: string, generateFn: typeof GenerateNextMessage_Generic, options?: Partial<GenerateNextMessageOptions_Init>) {
	messages.push(new StoryMessageProto({role: "user", content: message, model: null}));

	let content = "";
	const opts = GenerateNextMessageOptions.New_UsingSettings({
		minNumberedListSize: 2, dynamicListNumbers: [], llmHistory_keepLastXNumberedLists: 3, temperature: 1,
		...options,

		prevMessages: messages,
		onTextSegment: nextTextSegment=>{
			content += nextTextSegment;
		},
	});
	await generateFn(opts);
	messages.push(new StoryMessageProto({role: "assistant", content, model: opts.model}));
	console.log("Response:", content);
}
export function LLMTest_Undo() {
	// remove last two messages (one for response, one for the last input)
	messages.pop();
	messages.pop();
	console.log("Last response (after undo):", messages.Last().content);
}